import type { CSSProperties, ReactElement } from 'react';
import { useSkin } from '../features/appearance/SkinProvider';
import { useSkinIconOverride } from './SkinIconOverride';

export type AppIconName =
  | 'conversations' | 'chat' | 'new-chat' | 'session-list' | 'presets' | 'settings'
  | 'messages' | 'contacts' | 'moments'
  | 'attach' | 'gallery' | 'search' | 'back' | 'close'
  | 'refresh' | 'menu' | 'send' | 'stop' | 'play' | 'pause'
  | 'edit' | 'copy' | 'delete' | 'download' | 'upload' | 'favorite'
  | 'chevron-down' | 'chevron-up' | 'chevron-left' | 'chevron-right' | 'check';

const FALLBACK_PATHS: Record<AppIconName, ReactElement> = {
  conversations: <><path d="M5 6.5h11.5a2.5 2.5 0 0 1 2.5 2.5v5a2.5 2.5 0 0 1-2.5 2.5H10l-4.2 3v-3H5A2.5 2.5 0 0 1 2.5 14V9A2.5 2.5 0 0 1 5 6.5Z" /><path d="M6 11h6" /></>,
  chat: <><path d="M4 5.5h16v11H9l-5 4v-15Z" /><path d="M8 9.5h8M8 13h5" /></>,
  'new-chat': <><path d="M5 4.5h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-6 3v-15a2 2 0 0 1 2-2Z" /><path d="M12 8v7M8.5 11.5h7" /></>,
  'session-list': <><path d="M3 4h4v4H3zM3 10h4v4H3zM3 16h4v4H3z" fill="currentColor" stroke="none" /><path d="M11 6h10M11 12h10M11 18h10" strokeWidth="3" /></>,
  presets: <><rect x="4" y="4" width="12" height="15" rx="2.5" /><path d="M8 4V2.8h11.2A2.8 2.8 0 0 1 22 5.6V16" /><circle cx="10" cy="10" r="2.2" /><path d="M7 16c.8-1.7 1.8-2.5 3-2.5s2.2.8 3 2.5" /></>,
  messages: <><path d="M4 5.5h16v10.5H9.5L5 20v-4H4V5.5Z" /><path d="M8 9h8M8 12.5h5" /></>,
  contacts: <><circle cx="12" cy="8" r="3.5" /><path d="M5.5 20c.7-4 2.8-6 6.5-6s5.8 2 6.5 6" /><path d="M18.5 5.5h3M20 4v3" /></>,
  moments: <><circle cx="12" cy="12" r="8.5" /><path d="m12 7 1.5 3.5L17 12l-3.5 1.5L12 17l-1.5-3.5L7 12l3.5-1.5L12 7Z" /></>,
  settings: <><circle cx="12" cy="12" r="3.2" /><path d="M12 2.8v2M12 19.2v2M2.8 12h2M19.2 12h2M5.5 5.5l1.4 1.4M17.1 17.1l1.4 1.4M18.5 5.5l-1.4 1.4M6.9 17.1l-1.4 1.4" /></>,
  attach: <path d="m9 12.5 5.7-5.7a3.2 3.2 0 1 1 4.5 4.5l-7.3 7.3a5 5 0 0 1-7-7l7.1-7.1" />,
  gallery: <><rect x="3" y="4" width="18" height="16" rx="3" /><circle cx="8.2" cy="9" r="1.5" /><path d="m4 17 5-5 3.5 3.5 2.2-2.2L20 18" /></>,
  search: <><circle cx="12" cy="12" r="9" /><ellipse cx="12" cy="12" rx="4" ry="9" /><path d="M3 12h18M5 6.5h14M5 17.5h14" /></>,
  back: <path d="M19 12H5m6-6-6 6 6 6" />,
  close: <path d="m6 6 12 12M18 6 6 18" />,
  refresh: <><path d="M19.5 9A8 8 0 1 0 20 15" /><path d="M20 4v5h-5" /></>,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  send: <><path d="m3.5 12 17-8-5.2 16-4.1-6.2L3.5 12Z" /><path d="m11.2 13.8 4.8-4.8" /></>,
  stop: <rect x="6" y="6" width="12" height="12" rx="2" />,
  play: <path d="m8 5 11 7-11 7V5Z" />,
  pause: <><path d="M8 5v14M16 5v14" /></>,
  edit: <><path d="m4 20 4.2-1 10.6-10.6a2.4 2.4 0 0 0-3.4-3.4L4.8 15.6 4 20Z" /><path d="m14 6 4 4" /></>,
  copy: <><rect x="8" y="8" width="12" height="13" rx="2" /><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" /></>,
  delete: <><path d="M4 7h16M9 3.5h6L16 7H8l1-3.5Z" /><path d="m6 7 1 13h10l1-13M10 11v5M14 11v5" /></>,
  download: <><path d="M12 3v11m-4-4 4 4 4-4M4 19h16" /></>,
  upload: <><path d="M12 20V9m-4 4 4-4 4 4M4 5h16" /></>,
  favorite: <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-2.9-5.6 2.9 1.1-6.2L3 9.6l6.2-.9L12 3Z" />,
  'chevron-down': <path d="m6 9 6 6 6-6" />,
  'chevron-up': <path d="m6 15 6-6 6 6" />,
  'chevron-left': <path d="m15 6-6 6 6 6" />,
  'chevron-right': <path d="m9 6 6 6-6 6" />,
  check: <path d="m5 12.5 4.2 4.2L19 7" />,
};

export interface AppIconProps {
  name: AppIconName;
  size?: number;
  className?: string;
  title?: string;
}

export function AppIcon({ name, size = 22, className, title }: AppIconProps): ReactElement {
  const { skin } = useSkin();
  const custom = useSkinIconOverride()?.[name] ?? skin.iconSources?.[name];
  if (custom) {
    const maskUrl = custom.startsWith('url(') ? custom : `url("${custom}")`;
    const style: CSSProperties = {
      width: size,
      height: size,
      display: 'inline-block',
      flex: '0 0 auto',
      backgroundColor: 'var(--color-icon)',
      WebkitMaskImage: maskUrl,
      maskImage: maskUrl,
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskSize: 'contain',
      maskSize: 'contain',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
    };
    return <span className={className} style={style} role={title ? 'img' : undefined} aria-label={title} aria-hidden={title ? undefined : true} />;
  }
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="var(--skin-icon-stroke, 1.75)"
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      style={{ color: 'var(--color-icon)', flex: '0 0 auto' }}
    >
      {FALLBACK_PATHS[name]}
    </svg>
  );
}

